import React from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LineChart, Line, ReferenceLine, Label } from "recharts";
import { TrendingUp, History, Activity, BarChart2 } from "lucide-react";
import { motion } from "framer-motion";

const HistoricalWorkforce = ({ data }) => {
    if (!data || data.length === 0) return null;

    return (
        <div className="mt-12">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
                {/* Header Section */}
                <div className="d-flex align-items-center justify-content-between mb-10">
                    <div className="d-flex align-items-center gap-2">
                        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                            <History size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 m-0">Utah Historical Workforce Evolution</h3>
                            <p className="text-xs text-gray-400 mt-1">Growth trends and economic ratios (2018-2024)</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <div className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-bold border border-emerald-100 uppercase tracking-wider">
                            Real-time Metrics
                        </div>
                        <div className="px-3 py-1.5 bg-amber-50 text-amber-600 rounded-full text-[10px] font-bold border border-amber-100 uppercase tracking-wider">
                            Market Insight
                        </div>
                    </div>
                </div>

                <div className="row g-5">
                    {/* CHART 1: VOLUME */}
                    <Col lg={7}>
                        <div className="mb-6">
                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Workforce Volume</h4>
                            <div className="h-[1px] w-12 bg-indigo-500 rounded-full"></div>
                        </div>

                        <div style={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={data} margin={{ top: 15, right: 10, left: 30, bottom: 40 }}>
                                    <defs>
                                        <linearGradient id="colorEmployment" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="year"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                                    >
                                        <Label value="Calendar Year" offset={-20} position="insideBottom" style={{ fontSize: '11px', fill: '#94a3b8', fontWeight: 600 }} />
                                    </XAxis>
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                                        tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
                                    >
                                        <Label value="Employment (Jobs)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: '11px', fill: '#94a3b8', fontWeight: 600 }} offset={0} />
                                    </YAxis>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="annual_avg_employment" name="Avg Employment" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorEmployment)" />
                                 
                                    <ReferenceLine x={2020} stroke="#ef4444" strokeDasharray="3 3" label={{ position: 'top', value: 'Pandemic Shift', fill: '#ef4444', fontSize: 10, fontWeight: 'bold' }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </Col>

                    {/* CHART 2: RATIOS */}
                    <Col lg={5}>
                        <div className="mb-6">
                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Economic Ratios</h4>
                            <div className="h-[1px] w-12 bg-emerald-500 rounded-full"></div>
                        </div>

                        <div style={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data} margin={{ top: 10, right: 10, left: 30, bottom: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="year"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                                    >
                                        <Label value="Calendar Year" offset={-20} position="insideBottom" style={{ fontSize: '11px', fill: '#94a3b8', fontWeight: 600 }} />
                                    </XAxis>
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                                        tickFormatter={(val) => `${(val * 100).toFixed(1)}%`}
                                    >
                                        <Label value="Ratio (%)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: '11px', fill: '#94a3b8', fontWeight: 600 }} offset={0} />
                                    </YAxis>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                        formatter={(val) => [`${(val * 100).toFixed(1)}%`, ""]}
                                    />
                                    <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: '10px', paddingBottom: '20px' }} />
                                    <Line type="monotone" dataKey="unemployment_rate" name="Unemployment" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                                    <Line type="monotone" dataKey="participation_rate" name="Participation" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Col>
                </div>

                <div className="mt-10 p-5 bg-gray-50 rounded-2xl border border-gray-100 d-flex gap-4 align-items-start">
                    <div className="p-2 bg-white rounded-xl shadow-sm italic text-indigo-600 border border-indigo-50">
                        <Activity size={18} />
                    </div>
                    <div>
                        <h5 className="text-xs font-bold text-gray-700 mb-1 uppercase tracking-tight">Market Insight: The Elasticity Gap</h5>
                        <p className="text-xs text-gray-500 m-0 leading-relaxed">
                            Utah's construction sector showed remarkable resilience. While unemployment doubled in 2020, participation rates remained among the highest in the US, suggesting a rapid skill-pivot rather than market exits.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Col = ({ lg, children }) => (
    <div className={`col-lg-${lg}`}>
        {children}
    </div>
);

export default HistoricalWorkforce;
